import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { ConfigurablePageComponent } from 'src/app/core/abstracts/configurable-page.component'
import { IAsset } from 'src/app/core/interfaces/asset.interface'
import { FilterService } from 'src/app/core/services/filter.service'
import { LogService } from 'src/app/core/services/log.service'
import { SearchService } from 'src/app/core/services/search.service'
import { ChipsService } from 'src/app/core/services/chips.service'
import { EventService } from 'src/app/core/services/event.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { SeoService } from 'src/app/core/services/seo.service'
import { scrollToTop } from 'src/app/core/utils/scroll-to-top.util'
import { FilterId } from 'src/app/core/interfaces/filter.interface'
import { AssetService } from 'src/app/core/services/asset.service'
import { take } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { FilterContainerComponent } from 'src/app/components/filter-container/filter-container.component'
import { FooterComponent } from 'src/app/components/footer/footer.component'
import { HeaderComponent } from 'src/app/components/header/header.component'
import { SearchGridComponent } from 'src/app/components/search-grid/search-grid.component'
import { SearchComponent } from 'src/app/components/search/search.component'
import { SortComponent } from 'src/app/components/sort/sort.component'
import { ComparisonService } from 'src/app/core/services/comparison.service'
import { ErrorComponent } from 'src/app/components/error/error.component'


interface ISortOption {
  field: string
  direction: 'asc' | 'desc'
}

const sortOptions: { [key: string]: ISortOption } = {
  featured: { field: 'is_featured', direction: 'desc' },
  has_approved_photos: { field: 'has_approved_photos', direction: 'desc' },
  price_desc: { field: 'list_price', direction: 'desc' },
  price_asc: { field: 'list_price', direction: 'asc' },
  year_desc: { field: 'year', direction: 'desc' },
  year_asc: { field: 'year', direction: 'asc' },
  hours_desc: { field: 'meter_hours', direction: 'desc' },
  hours_asc: { field: 'meter_hours', direction: 'asc' },
  machines_with_images: { field: 'has_approved_photos', direction: 'desc' },
  recently_added: { field: 'listing_date', direction: 'desc' },
  recently_updated: { field: 'updated_date', direction: 'desc' },
}


@Component({
  selector: 'app-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['../../../assets/page.scss', './search-page.component.scss'],
  providers: [SeoService],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    HeaderComponent,
    FooterComponent,
    SearchComponent,
    SearchGridComponent,
    FilterContainerComponent,
    SortComponent,
    MatProgressSpinnerModule,
    ErrorComponent,
  ],
})
export class SearchPageComponent extends ConfigurablePageComponent implements OnInit, OnDestroy {
  private _logger = inject(LogService)
  private _route = inject(ActivatedRoute)
  private _router = inject(Router)
  private _chipsService = inject(ChipsService)
  private _filterService = inject(FilterService)
  private _eventService = inject(EventService)
  private _destroyRef = inject(DestroyRef)
  private _seoService = inject(SeoService)
  private _assetService = inject(AssetService)
  protected searchService = inject(SearchService)
  protected comparisonService = inject(ComparisonService)
  readonly totalResults = $localize`:@@search-grid.total-results:{searchResultsTotal} Results`

  assets!: IAsset[]
  FilterId = FilterId
  isLoading = signal(false)
  showMobileFilters: boolean = false

  ngOnInit() {
    const config = this.globalDataService.config()
    this.pageConfig.set(config.page.search)
    this._route.queryParamMap.subscribe((params: ParamMap) => {
      this.isLoading.set(true)
      const ids = params.get('ids')
      if (ids) {
        // If a list of equipment IDs is provided, load only those assets regardless of filter
        // This is mainly for QA use.  If it gets extended into client use, this should probably
        // become part of the filter loading process
        const equipmentIds = ids.split(',').filter(id => id)
        const total = equipmentIds.length
        this._assetService.getAssetsById(total, equipmentIds).pipe(
          take(1),
        ).subscribe(response => {
          this.isLoading.set(false)
          if (response instanceof HttpErrorResponse) {
            this._logger.error('Unable to load assets.  response: ', response)
            return
          }
          this.assets = response.data
        })
        return
      }
      const filtersParam = params.get('filters')
      try {
        this._filterService.loadFilters(filtersParam)
      } catch (e) {
        this._logger.error(`Unable to decode filters. [ ${filtersParam} ] `, e)
        this._router.navigate([], {})
        return
      }
      this.searchService.sort = params.get('sort')
      this.searchService.pagination.index = parseInt(params.get('page') as string) - 1 || 0
      this.searchService.pagination.field = this.searchService.sort ? sortOptions[this.searchService.sort].field : undefined
      this.searchService.pagination.direction = this.searchService.sort ? sortOptions[this.searchService.sort].direction : undefined
      this.searchService.loadAssets().then((assets: IAsset[]) => {
        this.assets = assets
      }).finally(() => {
        this.isLoading.set(false)
      })
      scrollToTop()
    })
    this._eventService.filterClicked$.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(() => {
      this.showMobileFilters = !this.showMobileFilters
    })
    this._seoService.init(
      config.seo.data,
      this.pageConfig().seo.data,
    )
    scrollToTop()
  }

  ngOnDestroy(): void {
    this._filterService.clearFilterState()
    this._chipsService.clearChips()
  }

  onFilterClicked(): void {
    this.showMobileFilters = true
  }

  getFilterPanelMaxHeight(): string {
    return this.comparisonService.hasAssets() ? 'calc(100vh - 290px)' : 'calc(100vh - 160px)'
  }
}
